import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { Label } from "src/components/ui/Label";
import { cn } from "src/lib/utils";
import {
  fetchMathParameters,
  type MathBoardOption,
  type MathGradeOption,
} from "src/services/diagnosticService";

const fieldClass = cn(
  "h-11 w-full appearance-none rounded-md border border-input bg-white px-3 pr-10 text-sm text-[#3e1168]",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
  "disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-600"
);

interface MathParamSelectorProps {
  gradeId: string | null;
  boardId: string | null;
  onGradeChange: (id: string, label: string) => void;
  onBoardChange: (id: string, label: string) => void;
}

export function MathParamSelector({
  gradeId,
  boardId,
  onGradeChange,
  onBoardChange,
}: MathParamSelectorProps) {
  const [grades, setGrades] = useState<MathGradeOption[]>([]);
  const [boards, setBoards] = useState<MathBoardOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchMathParameters()
      .then((result) => {
        if (cancelled) return;
        setGrades(result.grades);
        setBoards(result.boards);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Grades and boards could not be loaded.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const boardsForGrade = boards.filter((board) => board.gradeId === gradeId);
  const labelCounts = new Map<string, number>();
  for (const grade of grades) {
    labelCounts.set(grade.label, (labelCounts.get(grade.label) ?? 0) + 1);
  }
  const displayLabel = (grade: MathGradeOption) =>
    (labelCounts.get(grade.label) ?? 0) > 1 ? `${grade.label} — ${grade.countryName}` : grade.label;

  const grouped = Array.from(
    grades.reduce((groups, grade) => {
      const group = groups.get(grade.countryId);
      if (group) group.options.push(grade);
      else groups.set(grade.countryId, { countryId: grade.countryId, label: grade.countryName, options: [grade] });
      return groups;
    }, new Map<number, { countryId: number; label: string; options: MathGradeOption[] }>())
  )
    .sort(([left], [right]) => left - right)
    .map(([, group]) => ({
      ...group,
      options: [...group.options].sort((left, right) => left.order - right.order),
    }));

  if (loading) return <p className="text-sm text-gray-600">Loading grades and boards…</p>;
  if (error) return <p className="text-sm text-red-700" role="alert">{error}</p>;

  return (
    <div className="flex flex-col gap-5">
      <div className="space-y-2">
        <Label htmlFor="math-grade">Grade or course</Label>
        <div className="relative">
          <select
            id="math-grade"
            value={gradeId ?? ""}
            onChange={(event) => {
              const next = grades.find((grade) => grade.id === event.target.value);
              onGradeChange(event.target.value, next ? displayLabel(next) : "");
            }}
            className={fieldClass}
          >
            <option value="" disabled>Choose a grade or course</option>
            {grouped.map((group) => (
              <optgroup key={group.countryId} label={group.label}>
                {group.options.map((grade) => (
                  <option key={grade.id} value={grade.id}>{displayLabel(grade)}</option>
                ))}
              </optgroup>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-rfpurple-700" aria-hidden="true" />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="math-board">Board or standard</Label>
        <div className="relative">
          <select
            id="math-board"
            value={boardId ?? ""}
            onChange={(event) => {
              const next = boardsForGrade.find((board) => board.id === event.target.value);
              onBoardChange(event.target.value, next?.label ?? "");
            }}
            disabled={!gradeId}
            className={fieldClass}
          >
            <option value="" disabled>{gradeId ? "Choose a board or standard" : "Choose a grade first"}</option>
            {boardsForGrade.map((board) => (
              <option key={board.id} value={board.id}>{board.label}</option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-rfpurple-700" aria-hidden="true" />
        </div>
      </div>
    </div>
  );
}

export default MathParamSelector;
